import {
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  TableContainer,
} from '@mui/material';

import { tss } from 'tss-react/mui';

import type { DocumentType } from '@/gql';
import { graphql } from '@/gql';

import { nonFalsey } from '@/utils/nonFalsey';

import { useFileSearch } from '@/modules/files/hooks/useFileSearch';

import { FilenameCell } from './file-row/FilenameCell';
import { FileLastModifiedCell } from './file-row/FileLastModifiedCell';

export const FILE_MANAGER_SEARCH_RESULT_TABLE_FRAGMENT = graphql(`
  fragment fileManagerSearchResultTableFields on FilesForOwner {
    relatedOwners {
      owner {
        ... on Group {
          _id
        }

        ... on Service {
          _id
        }
      }
    }
  }
`);

interface Props {
  filesForOwner: DocumentType<typeof FILE_MANAGER_SEARCH_RESULT_TABLE_FRAGMENT>;
  searchText: string;
}

const useStyles = tss.create(({ theme }) => ({
  loading: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(1),
  },
}));

export const FileManagerSearchResultTable = ({
  filesForOwner,
  searchText,
}: Props) => {
  const { classes } = useStyles();

  const ownerIds = filesForOwner.relatedOwners
    .map((owner) => owner.owner._id)
    .filter(nonFalsey);

  const { data, loading } = useFileSearch(ownerIds, searchText);

  const searchResults = data?.files?.filter(nonFalsey) ?? [];

  return (
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>
              {loading ? 'Searching' : 'Search results'} for &quot;
              {searchText}
              &quot;
            </TableCell>
            <TableCell>Last Modified</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {!loading && searchResults.length === 0 ? (
            searchText.length < 3 ? (
              <TableRow>
                <TableCell>Search term must be at least 3 characters</TableCell>
                <TableCell />
                <TableCell />
              </TableRow>
            ) : (
              <TableRow>
                <TableCell>No results to display</TableCell>
                <TableCell />
                <TableCell />
              </TableRow>
            )
          ) : (
            searchResults.map((file) => (
              <TableRow key={file._id || undefined}>
                <FilenameCell file={file} isSearchResult={true} />
                <FileLastModifiedCell file={file} />
                <TableCell />
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
      {loading && (
        <div className={classes.loading}>
          <CircularProgress />
        </div>
      )}
    </TableContainer>
  );
};
